/**
 * DataKeyInput Component - Reusable data key input with validation and suggestions
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Key, AlertCircle, Check, HelpCircle } from 'lucide-react'
import { Platform } from '../../../../../types/macros'

export interface DataKeyInputProps {
  value: string
  onChange: (value: string) => void
  platform?: Platform | null
  label?: string
  placeholder?: string
  showCommonKeys?: boolean
  required?: boolean
  error?: string
  helpText?: string
}

const DataKeyInput: React.FC<DataKeyInputProps> = ({
  value,
  onChange,
  platform,
  label = "Data Key",
  placeholder = "Enter data key name",
  showCommonKeys = true,
  required = true,
  error,
  helpText
}) => {
  const [isFocused, setIsFocused] = useState(false)
  
  // Platform-specific common keys
  const commonKeys = React.useMemo(() => {
    const baseKeys = ['first_name', 'last_name', 'email', 'username']
    
    switch (platform) {
      case Platform.SHOPIFY:
        return ['first_name', 'last_name']
      case Platform.MOMENTPERKS:
        return [...baseKeys, 'loyalty_points', 'tier_level', 'order_count']
      case Platform.PERKSWALL:
        return [...baseKeys, 'brand_name', 'user_segment', 'custom_field_1']
      default:
        return baseKeys
    }
  }, [platform])

  // Validation
  const isValid = React.useMemo(() => {
    if (!value) return !required
    
    // Basic validation: alphanumeric and underscores only
    const keyRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/
    return keyRegex.test(value)
  }, [value, required])

  const handleKeySelect = (key: string) => {
    onChange(key)
  }

  return (
    <div className="space-y-4">
      {/* Label and Helper */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {helpText && (
          <div className="group relative">
            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
            <div className="absolute right-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
              {helpText}
            </div>
          </div>
        )}
      </div>

      {/* Input Field */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <Key className="w-4 h-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`w-full pl-10 pr-10 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
            error
              ? 'border-red-300 focus:border-red-500'
              : isValid || !value
              ? 'border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215]'
              : 'border-red-300 focus:border-red-500'
          } bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white`}
        />
        
        {/* Validation Icon */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {value && (
            isValid ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-500" />
            )
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 text-sm text-red-600"
        >
          <AlertCircle className="w-4 h-4" />
          {error}
        </motion.div>
      )}

      {/* Validation Message */}
      {value && !isValid && !error && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 text-sm text-red-600"
        >
          <AlertCircle className="w-4 h-4" />
          Key must start with a letter and contain only letters, numbers, and underscores
        </motion.div>
      )}

      {/* Common Keys Suggestions */}
      {showCommonKeys && commonKeys.length > 0 && (isFocused || !value) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Common keys for {platform || 'this platform'}:
          </p>
          <div className="flex flex-wrap gap-2">
            {commonKeys.map((key) => (
              <button
                key={key}
                onClick={() => handleKeySelect(key)}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-[#FE4215]/10 text-gray-700 hover:text-[#FE4215] rounded-full border border-gray-200 hover:border-[#FE4215] transition-all"
              >
                {key}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Input Focus Helper */}
      {isFocused && !value && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-xs text-gray-500 dark:text-gray-400"
        >
          💡 Tip: Data keys should match the field names in your data (e.g., first_name, order_total)
        </motion.div>
      )}
    </div>
  )
}

export default DataKeyInput